import { useQuery } from '@tanstack/react-query';
import { orpc } from '../../../apis/orpc-query';
import { Timeline } from '../../../components/ui/Timeline';
import { formatDayTime } from '../../../lib/format';
import { useI18nTranslation } from '../../../lib/use-i18n-translation';

export interface VehicleActivityTabProps {
  vehicleId: string;
}

/**
 * Activity — the append-only history (plan §4, §7).
 *
 * The estimate's own rows were **copied onto the vehicle at the purchase**, keeping their timestamps, so
 * this tab opens with the negotiation that produced the car rather than with a single "purchased" line.
 * That is the point of copying rather than linking: the dossier is readable on its own, years later, even
 * if somebody is looking at it for the first time.
 */
export const VehicleActivityTab = ({ vehicleId }: VehicleActivityTabProps) => {
  const { t } = useI18nTranslation();
  const activityQuery = useQuery(orpc.vehicles.listActivity.queryOptions({ input: { vehicleId, page: 1, pageSize: 100 } }));

  /**
   * A line reads "what changed · why", in that order.
   *
   * A **correction** is the row that needs both: "salePrice: 15,000 → 15,500" is the change, and the
   * reason is the whole point of having recorded it (PP §4). Every other row that carries a reason and
   * no changes still prints the reason alone, exactly as before.
   */
  const lineOf = (event: {
    kind: string;
    changes: { field: string; from: unknown; to: unknown }[];
    fromStatus: string | null;
    toStatus: string | null;
    reason: string | null;
  }): string | undefined => {
    const changes = event.changes
      .map((change) => `${t(`estimates.fields.${change.field}`, { defaultValue: change.field })}: ${format(change.from)} → ${format(change.to)}`)
      .join(t('common.listSeparator'));

    const status =
      event.fromStatus === null && event.toStatus === null ? '' : `${statusLabel(event.fromStatus, t)} → ${statusLabel(event.toStatus, t)}`;

    const head = changes !== '' ? changes : status;
    const parts = [head, event.reason ?? ''].filter((part) => part !== '');

    return parts.length === 0 ? undefined : parts.join(' · ');
  };

  const items = (activityQuery.data?.items ?? []).map((event) => ({
    key: event.id,
    title: t(`activity.kinds.${event.kind}`, { defaultValue: event.kind }),
    detail: lineOf(event),
    meta: [formatDayTime(event.createdAt), event.actorName].filter((part) => part !== null && part !== '').join(' · '),
  }));

  return (
    <section className="space-y-4 rounded-lg border border-border bg-card p-5">
      <h2 className="text-sm font-semibold text-card-foreground">{t('vehicles.activityTitle')}</h2>
      <p className="text-sm text-muted-foreground">{t('vehicles.activityBody')}</p>
      {items.length === 0 ? <p className="text-sm text-muted-foreground">{t('table.empty')}</p> : <Timeline items={items} />}
    </section>
  );
};

/** An enum status is printed in the reader's language; anything else is left as it was recorded. */
const statusLabel = (status: string | null, t: (key: string, options?: Record<string, unknown>) => string): string => {
  if (status === null) {
    return '—';
  }

  const inventory = t(`enums.inventoryStatus.${status}`, { defaultValue: '' });

  return inventory !== '' ? inventory : t(`estimates.statuses.${status}`, { defaultValue: status });
};

const format = (value: unknown): string => (value === null || value === undefined || value === '' ? '—' : String(value));
